import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Ship } from 'src/app/model';
import { RestaurantBody, RestaurantDetail, ShiftBody } from 'src/app/model/mdr.model';
import { MdrService } from 'src/app/services/mdr.service';
import { SpinnerService } from 'src/app/_components/spinner/spinner.service';
import { ModalErrorComponent } from 'src/app/modal/modal-gestioneErrori/modal-error/modal-error.component';
import { GenericConfirmationComponent } from 'src/app/modal/generic-confirmation/generic-confirmation.component';
import { MyErrorHandler } from 'src/app/constants/error';

/** Backend error code returned when the restaurant code is already used on that ship. */
const DUPLICATE_CODE_ERROR = 'RESTAURANT_CODE_ALREADY_IN_USE';

/** Strict 24h HH:MM, e.g. rejects "99:71". */
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface RestaurantConfigDialogData {
  mode: 'create' | 'edit';
  restaurant?: RestaurantDetail;
  ships: Ship[];
}

/** Each shift's end time must be strictly later than its start time (HH:MM strings sort lexically). */
const endAfterStart: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const start = group.get('start')?.value;
  const end = group.get('end')?.value;
  if (!start || !end) return null;
  return end > start ? null : { endBeforeStart: true };
};

/** No two shifts in the restaurant may share the same code. */
const uniqueShiftCodes: ValidatorFn = (array: AbstractControl): ValidationErrors | null => {
  const groups = (array as FormArray).controls;
  const seen = new Set<string>();
  for (const g of groups) {
    const code = (g.get('code')?.value ?? '').trim().toUpperCase();
    if (!code) continue;
    if (seen.has(code)) return { duplicateShiftCode: true };
    seen.add(code);
  }
  return null;
};

@Component({
  selector: 'app-restaurant-config-dialog',
  templateUrl: './restaurant-config-dialog.component.html',
  styleUrls: ['./restaurant-config-dialog.component.scss'],
  standalone: false,
})
export class RestaurantConfigDialogComponent implements OnInit, OnDestroy {

  form!: FormGroup;

  /** Shown only after the user attempts to delete the last remaining shift. */
  showMinShiftError = false;

  private ngUnsubscribe = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<RestaurantConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RestaurantConfigDialogData,
    private mdrService: MdrService,
    private spinner: SpinnerService,
    private dialog: MatDialog,
  ) {}

  get isEdit(): boolean {
    return this.data.mode === 'edit';
  }

  get shifts(): FormArray {
    return this.form.get('shifts') as FormArray;
  }

  ngOnInit(): void {
    const r = this.data.restaurant;
    this.form = this.fb.group({
      code: [{ value: r?.code ?? '', disabled: this.isEdit }, [Validators.required, Validators.maxLength(1)]],
      name: [r?.name ?? '', [Validators.required, Validators.maxLength(50)]],
      ship: [{ value: r?.ship_id ?? '', disabled: this.isEdit }, [Validators.required]],
      shifts: this.fb.array([], uniqueShiftCodes),
    });

    this.populateShiftsFromData();

    // The duplicate-code conflict depends on code + ship: clear the server-set
    // error as soon as the user changes either, re-enabling SAVE.
    this.form.get('ship')!.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.clearDuplicateCodeError());
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /** (Re)builds the shifts FormArray from the restaurant data, or a single empty shift. */
  private populateShiftsFromData(): void {
    this.shifts.clear();
    const r = this.data.restaurant;
    if (r?.shifts?.length) {
      r.shifts.forEach(s => this.shifts.push(this.buildShiftGroup({
        id: s.id,
        code: s.code ?? '',
        start: s.start,
        end: s.end,
      })));
    } else {
      this.addShift();
    }
  }

  /** Restores every field to the state loaded on open (used when the conflict save is cancelled). */
  private resetFormToInitial(): void {
    const r = this.data.restaurant;
    this.form.reset({
      code: r?.code ?? '',
      name: r?.name ?? '',
      ship: r?.ship_id ?? '',
    });
    this.populateShiftsFromData();
    this.showMinShiftError = false;
  }

  addShift(): void {
    this.shifts.push(this.buildShiftGroup());
    this.shifts.markAsDirty();
    this.showMinShiftError = false;
  }

  removeShift(index: number): void {
    if (this.shifts.length <= 1) {
      this.showMinShiftError = true; // at least one shift required
      return;
    }
    this.shifts.removeAt(index);
    this.shifts.markAsDirty();
  }

  shiftHasTimeError(index: number): boolean {
    const group = this.shifts.at(index);
    return !!group.errors?.['endBeforeStart'] && group.touched;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEdit) {
      const stages = this.modifiedShiftStages();
      if (stages.length) {
        this.checkConflictsThenSave(stages);
        return;
      }
    }
    this.performSave();
  }

  /** Codes of original shifts that were changed or removed (these are the at-risk stages). */
  private modifiedShiftStages(): string[] {
    const original = this.data.restaurant?.shifts ?? [];
    const current: ShiftBody[] = this.form.getRawValue().shifts;
    const currentById = new Map<number, ShiftBody>();
    current.forEach(s => { if (s.id != null) currentById.set(s.id, s); });

    const stages = original.reduce<string[]>((acc, o) => {
      const code = (o.code ?? '').trim();
      if (!code) return acc;
      const cur = currentById.get(o.id);
      const changed = !cur
        || (cur.code ?? '').trim() !== code
        || cur.start !== o.start
        || cur.end !== o.end;
      if (changed) acc.push(code);
      return acc;
    }, []);
    return [...new Set(stages)];
  }

  private checkConflictsThenSave(stages: string[]): void {
    const shipId = this.data.restaurant!.ship_id;
    const restaurantCode = this.data.restaurant!.code;
    this.spinner.show();
    forkJoin(stages.map(stage =>
      this.mdrService.checkRestaurantConflict(shipId, restaurantCode, stage)))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (results) => {
          this.spinner.hide();
          const conflicting = stages.filter((_, i) => results[i].conflict);
          if (conflicting.length) {
            this.confirmConflictThenSave(conflicting);
          } else {
            this.performSave();
          }
        },
        error: (err) => {
          this.spinner.hide();
          this.dialog.open(ModalErrorComponent, { data: MyErrorHandler.getText(err, true) });
        },
      });
  }

  private confirmConflictThenSave(stages: string[]): void {
    const stageList = stages.map(s => `"${s}"`).join(', ');
    const ref = this.dialog.open(GenericConfirmationComponent, {
      data: {
        message: `Warning: there are pending MDR requests with seating code ${stageList} you are trying to change. Are you sure you want to modify it/them?`,
        english: true,
        warningAmber: true,
      },
    });
    ref.afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed: boolean) => {
        if (confirmed) this.performSave();
        else this.resetFormToInitial();
      });
  }

  /** The original HTTP create/update logic, extracted from save(). */
  private performSave(): void {
    const body = this.buildBody();
    const request$ = this.isEdit
      ? this.mdrService.updateRestaurant(this.data.restaurant!.id, body)
      : this.mdrService.createRestaurant(body);

    this.spinner.show();
    request$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (detail: RestaurantDetail) => {
          this.spinner.hide();
          this.dialogRef.close(detail);
        },
        error: (err) => this.handleSaveError(err),
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  private handleSaveError(err: any): void {
    this.spinner.hide();
    const codes: string[] = (err?.error?.errors ?? []).map((e: { code?: string }) => e?.code);
    if (codes.includes(DUPLICATE_CODE_ERROR)) {
      const codeCtrl = this.form.get('code')!;
      codeCtrl.setErrors({ duplicateCode: true });
      codeCtrl.markAsTouched();
      return;
    }
    this.dialog.open(ModalErrorComponent, { data: MyErrorHandler.getText(err, true) });
  }

  /** Drop the server-set duplicate-code error without disturbing the field's own validators. */
  private clearDuplicateCodeError(): void {
    const codeCtrl = this.form.get('code')!;
    if (codeCtrl.hasError('duplicateCode')) {
      codeCtrl.updateValueAndValidity();
    }
  }

  private buildShiftGroup(value?: ShiftBody): FormGroup {
    const isSavedShift = value?.id != null;
    return this.fb.group({
      id: [value?.id ?? null],
      code: [{ value: value?.code ?? '', disabled: isSavedShift }, [Validators.required, Validators.maxLength(1)]],
      start: [value?.start ?? '', [Validators.required, Validators.pattern(TIME_PATTERN)]],
      end: [value?.end ?? '', [Validators.required, Validators.pattern(TIME_PATTERN)]],
    }, { validators: endAfterStart });
  }

  private buildBody(): RestaurantBody {
    const raw = this.form.getRawValue();
    return {
      code: raw.code.trim(),
      name: raw.name.trim(),
      ship: raw.ship,
      shifts: raw.shifts.map((s: ShiftBody) => {
        const shift: ShiftBody = { code: (s.code ?? '').trim(), start: s.start, end: s.end };
        if (s.id != null) shift.id = s.id; // keep id only in edit so backend diffs add/update/delete
        return shift;
      }),
    };
  }
}
